import { useBilling } from '../contexts/BillingContext';
import { isPooledBilling } from '../types/billing';

/**
 * THE billing-mode hook — the single place any surface outside the Billing
 * page asks which billing mode a workspace is on.
 *
 * Plan-TextVoicePoolBilling-2026-08. The authority is the backend's explicit
 * `text_pool_enabled` boolean (`back/api/billing.py:441`), read through
 * `isPooledBilling()`; this hook only adds the plumbing — `BillingProvider` is
 * mounted app-wide (`main.tsx:136`), so every authenticated page can reach the
 * flag without new props and without a new backend field.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 🚨 RETIREMENT CONTRACT — READ THIS BEFORE DELETING THE POOL FLAG.
 *
 * DELETING THIS FILE IS THE INTENDED DEMOLITION LEVER, and it is meant to be
 * pulled. When the pool becomes the only billing mode, `rm` this module: every
 * remaining legacy-billing branch in the app becomes a TypeScript error, and
 * the compiler hands you the exact list of displays to delete. That is the
 * whole reason the question is asked HERE and not with an ad-hoc
 * `billing?.text_pool_enabled` check at each site — a scattered check leaves
 * the retirement to a human re-running a grep, and the grep MISSED TWO SITES
 * the first time this sweep was done (2026-08-20).
 *
 * So: never add a second way to ask this question. If you are about to write
 * `text_pool_enabled` or `isPooledBilling` anywhere outside the Billing page's
 * own components, use this hook instead.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * 🚨 THREE STATES, AND `loading` IS A REAL ONE — that is why this returns a
 * union and NOT a boolean. A boolean has to fold `loading` into one of the
 * other two silently, and whichever way it folds becomes a hidden default that
 * the next caller inherits without ever deciding. Naming the state forces the
 * decision at each call site and makes a `switch` exhaustive.
 *
 *   'loading' — the billing payload has not resolved. The mode is UNKNOWN.
 *   'pooled'  — pooled Text billing. Chat credits are not a billing unit.
 *   'legacy'  — per-category credit billing. Pre-pool behaviour, unchanged.
 *
 * FOR LEGACY COPY THE PREDICATE IS `mode === 'legacy'`, never `!== 'pooled'`.
 * Unknown is not legacy: rendering legacy copy while the payload is in flight
 * puts "Used 4 chat credits" on screen and retracts it a beat later — the same
 * false billing claim this gating exists to remove, in a shorter window. It
 * costs nothing to wait; billing resolves on app mount, long before an
 * Orchestrator turn completes.
 *
 * 🚨 SCOPE — this gates the RETIRED units only, i.e.
 * `back/utils/text_pool.py::POOL_RETIRED_CASCADE_RESOURCES` (chat conversations,
 * email threads, knowledge summaries, web scrapes, reports). VOICE MINUTES AND
 * LIVEAVATAR MINUTES KEEP THE CREDIT/BUNDLE CASCADE IN BOTH FLAG STATES,
 * deliberately — their credit and bundle copy is still TRUE under the pool and
 * must NOT be gated on this. If you are about to hide a Voice or LiveAvatar
 * credit display with this hook, you have the wrong hook.
 */
export type BillingMode = 'loading' | 'pooled' | 'legacy';

export const useBillingMode = (): BillingMode => {
  const { billing } = useBilling();
  if (billing === null) return 'loading';
  return isPooledBilling(billing) ? 'pooled' : 'legacy';
};

/**
 * Exhaustive branch over the three states, for callers that genuinely render
 * something different in each — the type will not let you forget `loading`.
 *
 * Most call sites do NOT need this: gating one paragraph is `mode === 'legacy'`
 * and that is the clearer thing to read. Reach for this only when `loading`
 * needs its own output (a skeleton, say) rather than simply rendering nothing.
 */
export function matchBillingMode<T>(
  mode: BillingMode,
  arms: { loading: () => T; pooled: () => T; legacy: () => T },
): T {
  return arms[mode]();
}

export default useBillingMode;
